import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Aperture, Image as ImageIcon, RefreshCw, X } from 'lucide-react';
import type { ScanAttachment } from '../types';

interface Props {
  open: boolean;
  batchStickyMode: boolean;
  /** Photos attached so far (shown on the Done button). */
  shotCount: number;
  onCapture: (scan: ScanAttachment) => void;
  onClose: () => void;
  /** Fallback to the gallery/file picker (camera unavailable or by choice). */
  onGallery: () => void;
}

/** Full-screen document camera with crop guide, capture, and flip. */
export function ScanCameraModal({ open, batchStickyMode, shotCount, onCapture, onClose, onGallery }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }
    let cancelled = false;
    (async () => {
      stop();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      } catch {
        // No camera / permission denied → gallery picker instead.
        onClose();
        onGallery();
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    onCapture({
      mimeType: 'image/jpeg',
      dataBase64: dataUrl.split(',')[1] ?? '',
      previewUrl: dataUrl,
      fileName: `scan-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.jpg`,
      batchStickyMode,
    });
    // Stay open — snap as many pages/notes as needed, then tap Done.
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex flex-col justify-between bg-slate-950 p-5"
        >
          <div className="flex items-center justify-between pb-2 text-white">
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
              <Aperture size={15} className="text-brand-400" />
              Document Scanner
            </span>
            <div className="flex items-center gap-2">
              {shotCount > 0 && (
                <button
                  onClick={onClose}
                  className="rounded-xl bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500"
                >
                  Done · {shotCount} photo{shotCount === 1 ? '' : 's'}
                </button>
              )}
              <button
                onClick={onClose}
                aria-label="Close scanner"
                className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-400 transition-colors hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="relative my-4 flex-1 overflow-hidden rounded-3xl border border-slate-800/80 bg-black shadow-2xl">
            <video ref={videoRef} autoPlay playsInline className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-8 flex flex-col justify-between rounded-2xl border-2 border-dashed border-brand-400 p-4">
              <div className="flex justify-between">
                <div className="h-5 w-5 rounded-tl border-l-4 border-t-4 border-brand-400" />
                <div className="h-5 w-5 rounded-tr border-r-4 border-t-4 border-brand-400" />
              </div>
              <p className="mx-auto max-w-[220px] rounded-xl border border-brand-500/20 bg-slate-950/80 p-2.5 text-center text-[9px] font-bold uppercase tracking-wide text-brand-400">
                {batchStickyMode
                  ? 'Fit sticky notes in the frame — snap as many photos as you need'
                  : 'Align the page in the frame — snap as many photos as you need'}
              </p>
              <div className="flex justify-between">
                <div className="h-5 w-5 rounded-bl border-b-4 border-l-4 border-brand-400" />
                <div className="h-5 w-5 rounded-br border-b-4 border-r-4 border-brand-400" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-around rounded-2xl border border-slate-800 bg-slate-900 py-5 shadow-xl">
            <button
              onClick={() => {
                onClose();
                onGallery();
              }}
              className="flex flex-col items-center gap-1 text-slate-400 transition-colors hover:text-white"
            >
              <ImageIcon size={20} />
              <span className="text-[9px] font-bold uppercase tracking-wider">Gallery</span>
            </button>
            <button
              onClick={capture}
              aria-label="Capture"
              className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-brand-600/50 bg-white shadow-2xl transition-transform active:scale-90"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-white">
                <Aperture size={20} />
              </span>
            </button>
            <button
              onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
              className="flex flex-col items-center gap-1 text-slate-400 transition-colors hover:text-white"
            >
              <RefreshCw size={20} />
              <span className="text-[9px] font-bold uppercase tracking-wider">Flip</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
